import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { StatusBadge } from '@/components/StatusBadge';
import { AddDomainDialog } from '@/components/AddDomainDialog';
import { ImportCSVDialog } from '@/components/ImportCSVDialog';
import { EditDomainDialog } from '@/components/EditDomainDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Search, Loader2, RefreshCw, Trash2, Download, ExternalLink, ArrowUpDown, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getExpiryStatus, formatDate } from '@/lib/utils';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Dashboard() {
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [registrarFilter, setRegistrarFilter] = useState('all');
  const [sortBy, setSortBy] = useState('expiration');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_URL}/domains`);
      setDomains(response.data);
      setFilteredDomains(response.data);
    } catch (error) {
      toast.error('Failed to fetch domains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    let filtered = domains;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(domain =>
        domain.domain_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        domain.registrar?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        domain.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(domain => {
        const status = getExpiryStatus(domain.expiration_date).status;
        return status === statusFilter;
      });
    }

    // Apply registrar filter
    if (registrarFilter !== 'all') {
      filtered = filtered.filter(domain => domain.registrar === registrarFilter);
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.domain_name.localeCompare(b.domain_name);
          break;
        case 'expiration':
          const dateA = a.expiration_date ? new Date(a.expiration_date) : new Date(0);
          const dateB = b.expiration_date ? new Date(b.expiration_date) : new Date(0);
          comparison = dateA - dateB;
          break;
        case 'registrar':
          comparison = (a.registrar || '').localeCompare(b.registrar || '');
          break;
        case 'added':
          const createdA = new Date(a.created_at);
          const createdB = new Date(b.created_at);
          comparison = createdA - createdB;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredDomains(filtered);
  }, [searchQuery, statusFilter, registrarFilter, sortBy, sortOrder, domains]);

  const handleRefresh = async (domainId) => {
    setRefreshingId(domainId);
    try {
      await axios.post(`${API_URL}/domains/${domainId}/refresh`);
      toast.success('Domain refreshed successfully');
      await fetchDomains();
    } catch (error) {
      toast.error('Failed to refresh domain');
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDelete = async (domainId) => {
    try {
      await axios.delete(`${API_URL}/domains/${domainId}`);
      toast.success('Domain deleted successfully');
      await fetchDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/domains/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'domains.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Domains exported successfully');
    } catch (error) {
      toast.error('Failed to export domains');
    }
  };

  const handleStatusCardClick = (status) => {
    if (statusFilter === status) {
      setStatusFilter('all');
    } else {
      setStatusFilter(status);
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setRegistrarFilter('all');
    setSearchQuery('');
  };

  const stats = {
    total: domains.length,
    critical: domains.filter(d => getExpiryStatus(d.expiration_date).status === 'critical').length,
    warning: domains.filter(d => getExpiryStatus(d.expiration_date).status === 'warning').length,
    safe: domains.filter(d => getExpiryStatus(d.expiration_date).status === 'safe').length
  };

  const uniqueRegistrars = ['all', ...new Set(domains.map(d => d.registrar).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <h1 className="text-4xl font-black font-heading tracking-tight mb-2" data-testid="dashboard-title">
              Domain Monitor
            </h1>
            <p className="text-muted-foreground font-body">
              Track and manage domain expiration dates
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card 
              className={`p-6 border-border/50 backdrop-blur-sm cursor-pointer transition-all hover:scale-105 ${
                statusFilter === 'all' ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setStatusFilter('all')}
              data-testid="total-domains-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
                Total Domains
              </div>
              <div className="text-3xl font-black font-heading">{stats.total}</div>
            </Card>
            <Card 
              className={`p-6 border-border/50 backdrop-blur-sm border-l-4 border-l-red-500/50 cursor-pointer transition-all hover:scale-105 ${
                statusFilter === 'critical' ? 'ring-2 ring-red-500' : ''
              }`}
              onClick={() => handleStatusCardClick('critical')}
              data-testid="critical-domains-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-2">
                Critical (&lt;7d)
              </div>
              <div className="text-3xl font-black font-heading text-red-500">{stats.critical}</div>
            </Card>
            <Card 
              className={`p-6 border-border/50 backdrop-blur-sm border-l-4 border-l-yellow-500/50 cursor-pointer transition-all hover:scale-105 ${
                statusFilter === 'warning' ? 'ring-2 ring-yellow-500' : ''
              }`}
              onClick={() => handleStatusCardClick('warning')}
              data-testid="warning-domains-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-2">
                Warning (&lt;30d)
              </div>
              <div className="text-3xl font-black font-heading text-yellow-500">{stats.warning}</div>
            </Card>
            <Card 
              className={`p-6 border-border/50 backdrop-blur-sm border-l-4 border-l-green-500/50 cursor-pointer transition-all hover:scale-105 ${
                statusFilter === 'safe' ? 'ring-2 ring-green-500' : ''
              }`}
              onClick={() => handleStatusCardClick('safe')}
              data-testid="safe-domains-card"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-green-500 mb-2">
                Safe (&gt;30d)
              </div>
              <div className="text-3xl font-black font-heading text-green-500">{stats.safe}</div>
            </Card>
          </div>

          <Card className="border-border/50 backdrop-blur-sm" data-testid="domains-table-card">
            <div className="p-6 border-b border-border/50">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search domains, registrars, tags..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 font-mono"
                      data-testid="search-input"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-button">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                    <ImportCSVDialog onImportComplete={fetchDomains} />
                    <AddDomainDialog onDomainAdded={fetchDomains} />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <div className="flex gap-2 flex-wrap items-center">
                    <Select value={registrarFilter} onValueChange={setRegistrarFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by Registrar" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueRegistrars.map(reg => (
                          <SelectItem key={reg} value={reg}>
                            {reg === 'all' ? 'All Registrars' : reg}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expiration">Expiration Date</SelectItem>
                        <SelectItem value="name">Domain Name</SelectItem>
                        <SelectItem value="registrar">Registrar</SelectItem>
                        <SelectItem value="added">Date Added</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      <ArrowUpDown className="h-4 w-4 mr-2" />
                      {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                    </Button>

                    {(statusFilter !== 'all' || registrarFilter !== 'all' || searchQuery) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  
                  <div className="text-sm text-muted-foreground font-mono">
                    Showing {filteredDomains.length} of {domains.length} domains
                  </div>
                </div>
              </div>
            </div>

            {filteredDomains.length === 0 ? (
              <div className="p-12 text-center" data-testid="empty-state">
                <img
                  src="https://images.unsplash.com/photo-1748158962286-36e6f7322939?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="No domains"
                  className="w-32 h-32 mx-auto mb-4 opacity-20 grayscale"
                />
                <p className="text-muted-foreground font-body">
                  {searchQuery || statusFilter !== 'all' || registrarFilter !== 'all' 
                    ? 'No domains match your filters' 
                    : 'No domains added yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono uppercase text-xs tracking-widest">Domain</TableHead>
                      <TableHead className="font-mono uppercase text-xs tracking-widest">Registrar</TableHead>
                      <TableHead className="font-mono uppercase text-xs tracking-widest">Expiry Date</TableHead>
                      <TableHead className="font-mono uppercase text-xs tracking-widest">Status</TableHead>
                      <TableHead className="font-mono uppercase text-xs tracking-widest">Tags</TableHead>
                      <TableHead className="font-mono uppercase text-xs tracking-widest text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDomains.map((domain) => {
                      const expiryStatus = getExpiryStatus(domain.expiration_date);
                      return (
                        <TableRow key={domain.id} data-testid={`domain-row-${domain.id}`}>
                          <TableCell className="font-mono font-medium">
                            <div className="flex items-center gap-2">
                              {domain.domain_name}
                              {domain.renewal_link && (
                                <a
                                  href={domain.renewal_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary/80"
                                  data-testid="renewal-link"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-body text-sm text-muted-foreground">
                            {domain.registrar || 'Unknown'}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatDate(domain.expiration_date)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={expiryStatus.status} daysUntil={expiryStatus.daysUntil} />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {domain.tags.map((tag, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <EditDomainDialog domain={domain} onDomainUpdated={fetchDomains} />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRefresh(domain.id)}
                                disabled={refreshingId === domain.id}
                                data-testid={`refresh-button-${domain.id}`}
                              >
                                <RefreshCw className={`h-4 w-4 ${refreshingId === domain.id ? 'animate-spin' : ''}`} />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`delete-button-${domain.id}`}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Domain</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete {domain.domain_name}? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(domain.id)}>
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
